import {
  Box,
  Button,
  FormHelperText,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import userService from '../../../services/userService';
import securityUtils from '../../../services/securityUtils';
import { usePortalName } from '../../../hooks/usePortalName';
import { VisibilityOffOutlined, VisibilityOutlined } from '@mui/icons-material';
import PasswordRequirements from './PasswordRequirements';

const PREFIX = 'ResetPasswordPageForm';

const classes = {
  container: `${PREFIX}-container`,
  form: `${PREFIX}-form`,
  title: `${PREFIX}-title`,
  resetTitle: `${PREFIX}-resetTitle`,
  subtitle: `${PREFIX}-subtitle`,
  submit: `${PREFIX}-submit`,
  next: `${PREFIX}-next`,
  backButton: `${PREFIX}-backButton`,
  formContainer: `${PREFIX}-formContainer`,
  formPage: `${PREFIX}-formPage`,
  formLarge: `${PREFIX}-formLarge`,
  password: `${PREFIX}-password`,
  passwordWrapper: `${PREFIX}-passwordWrapper`,
  inlineError: `${PREFIX}-inlineError`,
  link: `${PREFIX}-link`,
  largeEmailIcon: `${PREFIX}-largeEmailIcon`,
  largeCheckIcon: `${PREFIX}-largeCheckIcon`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.container}`]: {
    backgroundColor: 'white',
    padding: theme.spacing(4),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[5],
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },

  [`& .${classes.form}`]: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
  },

  [`& .${classes.password}`]: {
    width: '100%',
  },

  [`& .${classes.title}`]: {
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(1),
    color: theme.palette.lightTextPrimary.main,
  },

  [`& .${classes.resetTitle}`]: {
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.subtitle}`]: {
    textAlign: 'start',
    width: '100%',
    marginBottom: theme.spacing(2),
    color: theme.palette.lightTextPrimary.main,
  },

  [`& .${classes.formLarge}`]: {
    marginTop: theme.spacing(1),
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
  },

  [`& .${classes.submit}`]: {
    width: '100%',
    marginTop: theme.spacing(2.5),
  },

  [`& .${classes.next}`]: {
    width: '100%',
    marginTop: theme.spacing(2),
  },

  [`& .${classes.backButton}`]: {
    position: 'absolute',
    top: theme.spacing(1),
    left: theme.spacing(1),
    width: 40,
    height: 40,
  },

  [`& .${classes.formPage}`]: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },

  [`& .${classes.inlineError}`]: {
    marginTop: theme.spacing(-2),
    paddingLeft: theme.spacing(0.5),
    fontSize: 14,
    width: '100%',
  },

  [`& .${classes.link}`]: {
    color: theme.palette.buttonColor.main,
    textDecoration: 'none',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 400,
    display: 'inline-block',
    marginTop: theme.spacing(3),
    '&:hover': {
      textDecoration: 'underline',
    },
  },

  [`.${classes.largeEmailIcon}`]: {
    width: 144,
    height: 144,
    backgroundColor: theme.palette.lightActionDisabled54p.main,
    WebkitMaskImage: 'url(/email_large.svg)',
    maskImage: 'url(/email_large.svg)',
    maskRepeat: 'no-repeat',
    maskSize: '80%',
    maskPosition: 'center',
    margin: '0 auto',
  },

  [`.${classes.largeCheckIcon}`]: {
    width: 144,
    height: 144,
    backgroundColor: theme.palette.lightActionDisabled54p.main,
    WebkitMaskImage: 'url(/check_large.svg)',
    maskImage: 'url(/check_large.svg)',
    maskRepeat: 'no-repeat',
    maskSize: '90%',
    maskPosition: 'center',
    margin: '0 auto',
  },
}));

const StyledBox = styled(Box)({
  whiteSpace: 'nowrap',
  fontWeight: '600',
});

enum ResetPasswordPageFormSteps {
  REQUEST_RESET_EMAIL = 1,
  RESEND_RESET_EMAIL = 2,
  SET_NEW_PASSWORD = 3,
  PASSWORD_CHANGED = 4,
}

export default function ResetPasswordPageForm() {
  const { t } = useTranslation('common');
  const [searchParams, setSearchParams] = useSearchParams();
  const { register, handleSubmit, formState } = useForm();
  const navigate = useNavigate();
  const portalName = usePortalName();
  const [step, setStep] = useState<ResetPasswordPageFormSteps>(
    ResetPasswordPageFormSteps.REQUEST_RESET_EMAIL,
  );
  const [resetEmail, setResetEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [backToLoginUrl, setBackToLoginUrl] = useState<string>('/login');
  const [isClientPortal, setIsClientPortal] = useState<boolean>(false);

  React.useEffect(() => {
    const token = searchParams.get('passwordResetToken');
    const clientPortalFlag = searchParams.get('isClientPortal') === 'true';
    if (clientPortalFlag) {
      setIsClientPortal(true);
    }
    if (token) {
      setStep(ResetPasswordPageFormSteps.SET_NEW_PASSWORD);
    }
  }, [searchParams]);

  React.useEffect(() => {
    if (isClientPortal) {
      setBackToLoginUrl('/clientLogin');
    } else {
      setBackToLoginUrl('/login');
    }
  }, [isClientPortal]);

  const handleResetPasswordSubmit = (data: any) => {
    const email = data.email;
    const callbackUrl = window.location.href;
    const logoUrl = securityUtils.getPortalLogoURL(portalName || '');
    const clientBC = securityUtils.getPortalClientBC(portalName || '');
    setResetEmail(email);
    userService
      .forgotMemberPassword(email, callbackUrl, logoUrl, clientBC)
      .subscribe({
        next() {
          setStep(ResetPasswordPageFormSteps.RESEND_RESET_EMAIL);
          setErrorMessage('');
        },
        error(err) {
          if (err.message) {
            setErrorMessage(String(err.message) || '');
          } else {
            setErrorMessage(String(err) || '');
          }
        },
      });
  };

  const handleResendPasswordSubmit = () => {
    const email = resetEmail;
    const callbackUrl = window.location.href;
    const logoUrl = securityUtils.getPortalLogoURL(portalName || '');
    const clientBC = securityUtils.getPortalClientBC(portalName || '');
    userService
      .forgotMemberPassword(email, callbackUrl, logoUrl, clientBC)
      .subscribe({
        next() {
          setStep(ResetPasswordPageFormSteps.RESEND_RESET_EMAIL);
          setErrorMessage('');
        },
        error(err) {
          if (err.message) {
            setErrorMessage(String(err.message) || '');
          } else {
            setErrorMessage(String(err) || '');
          }
        },
      });
  };

  const updatePassword = (data: any) => {
    const token = searchParams.get('passwordResetToken');
    userService.updatePassword(token || '', data.password).subscribe({
      next() {
        setStep(ResetPasswordPageFormSteps.PASSWORD_CHANGED);
        setErrorMessage('');
      },
      error(err) {
        if (err.message) {
          setErrorMessage(String(err.message) || '');
        } else {
          setErrorMessage(String(err) || '');
        }
      },
    });
  };

  return (
    <Root className={classes.container}>
      {step === ResetPasswordPageFormSteps.REQUEST_RESET_EMAIL && (
        <Box className={classes.formPage}>
          <Typography className={classes.title} variant="h3">
            {t('member.resetPasswordForm.resetYourPassword')}
          </Typography>
          <Typography className={classes.subtitle} variant="body1">
            {t('member.resetPasswordForm.enterEmailMessage')}
          </Typography>
          <form
            onSubmit={handleSubmit(handleResetPasswordSubmit)}
            className={classes.formLarge}>
            <TextField
              fullWidth
              label={t('client.email')}
              variant="outlined"
              type="email"
              required
              {...register('email', { required: true })}
            />
            {errorMessage && (
              <Typography variant="body2" color="error">
                {errorMessage}
              </Typography>
            )}
            <Button
              color="primary"
              variant="contained"
              size="large"
              className={classes.submit}
              type="submit">
              {t('member.continue')}
            </Button>
          </form>
          <Link href={backToLoginUrl} className={classes.link}>
            {t('member.backToLogin')}
          </Link>
        </Box>
      )}

      {step === ResetPasswordPageFormSteps.RESEND_RESET_EMAIL && (
        <Box className={classes.formPage}>
          <Box className={classes.largeEmailIcon} role="presentation" />
          <Typography className={classes.title} variant="h3">
            {t('member.resetPasswordForm.checkEmail')}
          </Typography>
          <Typography className={classes.subtitle}>
            <Trans
              i18nKey="member.resetPasswordForm.checkEmailMessage"
              values={{ email: resetEmail }}
              components={{ strong: <StyledBox component="span" /> }}
            />
          </Typography>
          <Button
            color="primary"
            variant="contained"
            size="large"
            className={classes.next}
            type="submit"
            onClick={handleResendPasswordSubmit}>
            {t('member.resetPasswordForm.resendEmail')}
          </Button>
          <Link href={backToLoginUrl} className={classes.link}>
            {t('member.backToLogin')}
          </Link>
        </Box>
      )}

      {step === ResetPasswordPageFormSteps.SET_NEW_PASSWORD && (
        <Box className={classes.formPage}>
          <Typography className={classes.resetTitle} variant="h3">
            {t('member.resetPasswordForm.resetYourPassword')}
          </Typography>
          <Typography className={classes.subtitle} variant="body1">
            {t('member.resetPasswordForm.enterPassword')}
          </Typography>
          <form
            onSubmit={handleSubmit(updatePassword)}
            className={classes.form}>
            <Box className={classes.password}>
              <TextField
                fullWidth
                label={t('member.resetPasswordForm.newPassword')}
                variant="outlined"
                required
                type={showPassword ? 'text' : 'password'}
                value={password}
                {...register('password', { required: true })}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={t('login.togglePassword')}>
                        {showPassword ? (
                          <VisibilityOffOutlined />
                        ) : (
                          <VisibilityOutlined />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {!!password && <PasswordRequirements password={password} />}
            </Box>

            <TextField
              fullWidth
              label={t('member.resetPasswordForm.confirmNewPassword')}
              variant="outlined"
              required
              type={showConfirmPassword ? 'text' : 'password'}
              {...register('confirmPassword', {
                required: true,
                validate: (value) =>
                  password === value || t('member.passwordMismatch'),
              })}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }>
                      {showConfirmPassword ? (
                        <VisibilityOffOutlined />
                      ) : (
                        <VisibilityOutlined />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {formState.errors['confirmPassword'] && (
              <FormHelperText error className={classes.inlineError}>
                {String(formState.errors['confirmPassword'].message || '')}
              </FormHelperText>
            )}
            {errorMessage && (
              <Typography
                variant="body2"
                color="error"
                className={classes.inlineError}>
                {errorMessage}
              </Typography>
            )}
            <Button
              color="primary"
              variant="contained"
              size="large"
              className={classes.next}
              type="submit">
              {t('member.resetPasswordForm.resetPassword')}
            </Button>
          </form>
        </Box>
      )}

      {step === ResetPasswordPageFormSteps.PASSWORD_CHANGED && (
        <Box className={classes.formPage}>
          <Box className={classes.largeCheckIcon} role="presentation" />
          <Typography className={classes.title} variant="h3">
            {t('member.resetPasswordForm.passwordChanged')}
          </Typography>
          <Typography className={classes.subtitle} variant="body1">
            {t('member.resetPasswordForm.passwordChangedSuccess', {
              email: resetEmail,
            })}
          </Typography>
          <Button
            color="primary"
            variant="contained"
            size="large"
            className={classes.next}
            onClick={() => navigate(backToLoginUrl)}>
            {t('member.backToLogin')}
          </Button>
        </Box>
      )}
    </Root>
  );
}
